// src/pages/PodcastImport.js

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { importPodcastFromRSS } from '../services/RssImportService';

function PodcastImport() {
  const [rssUrl, setRssUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const { currentUser, userProfile, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  
  // Remove the creator-only restriction - any authenticated user can import
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!rssUrl) {
      setError('Please enter a valid RSS feed URL');
      return;
    }

    if (userProfile?.isSuspended) {
      setError('Din konto er suspenderet — du kan ikke importere podcasts i øjeblikket.');
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const username = userProfile?.username || currentUser.email?.split('@')[0] || 'User';
      const result = await importPodcastFromRSS(rssUrl, currentUser.uid, username);
      
      if (result.success) {
        // Auto-promote to creator after successful import
        if (!userProfile?.isCreator) {
          try {
            await updateUserProfile({ 
              isCreator: true, 
              creatorSince: new Date(),
              lastUpdated: new Date()
            });
            
            // Wait a moment for the profile to update in context
            await new Promise(resolve => setTimeout(resolve, 500));
            
            setSuccess(result.message + ' 🎉 Welcome to Faircast Creators! Redirecting to your Creator Dashboard...');
          } catch (error) {
            console.error('Creator promotion failed:', error);
            setError('Podcast imported successfully, but failed to activate creator status. Please contact support.');
            return; // Don't redirect if promotion failed
          }
        } else {
          setSuccess(result.message + ' Your podcast was imported successfully! Redirecting...');
        }
        
        setRssUrl('');
        
        // Redirect to creator dashboard after brief delay
        setTimeout(() => {
          navigate('/creator-dashboard');
        }, 1500);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Failed to import podcast: ' + err.message);
    }
    
    setLoading(false);
  };
  
  return (
    <div className="podcast-import-container">
      <h2>Import Your Podcast from RSS</h2>
      <p className="import-intro">
        Already have a podcast? Import it instantly and become a Faircast creator! 
        We'll automatically set up your creator account once your RSS feed is verified.
      </p>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="rssUrl">Podcast RSS Feed URL</label>
          <input
            type="url"
            id="rssUrl"
            value={rssUrl}
            onChange={(e) => setRssUrl(e.target.value)}
            placeholder="https://example.com/feed.xml"
            required
          />
        </div>
        
        <button
          type="submit"
          className="primary-button"
          disabled={loading}
        >
          {loading ? 'Importing & Verifying...' : 'Import Podcast & Become Creator'}
        </button>
      </form>
      
      <div className="import-help">
        <h3>How to find your podcast RSS feed</h3>
        <p>Most podcast hosting platforms provide an RSS feed URL for your podcast. Look for "RSS feed" or "Distribution" in your podcast hosting dashboard.</p>
        <p>Common podcast hosts:</p>
        <ul>
          <li><strong>Anchor:</strong> Dashboard → Settings → Distribution</li>
          <li><strong>Spotify for Podcasters:</strong> Settings → Advanced → RSS Feed</li>
          <li><strong>Libsyn:</strong> Settings → Destinations</li>
          <li><strong>Buzzsprout:</strong> Settings → Website</li>
          <li><strong>Podbean:</strong> Settings → Basic Info</li>
          <li><strong>Apple Podcasts Connect:</strong> Your show → Analytics & Reports</li>
        </ul>
        
        <div className="creator-benefits">
          <h3>🎉 What happens after import?</h3>
          <ul>
            <li>✅ Instant creator account activation</li>
            <li>✅ Access to Creator Dashboard</li>
            <li>✅ 75% revenue share on all plays</li>
            <li>✅ Advanced analytics and insights</li>
            <li>✅ Direct listener engagement tools</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default PodcastImport;